import apiClient from './apiClient';

export interface Animal {
  id: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other';
  breed: string;
  type?: string;
  gender: 'male' | 'female';
  birthDate?: string;
  weight?: number;
  color: string;
  microchipId?: string;
  allergies?: string[];
  chronicConditions?: string[];
  medications?: string[];
  profileImage?: string;
  isActive: boolean;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  reproduced?: boolean;
  isNeutered?: boolean;
  neuteredDate?: string;
  status?: 'active' | 'deceased' | 'transferred';
  deceasedAt?: string;
  deceasedReason?: string;
}

export interface CreateAnimalRequest {
  name: string;
  species?: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other';
  breed: string;
  gender?: 'male' | 'female';
  birthDate?: string;
  weight?: number;
  color: string;
  microchipId?: string;
  allergies?: string[];
  chronicConditions?: string[];
  medications?: string[];
  profileImage?: string;
  reproduced?: boolean;
  isNeutered?: boolean;
  neuteredDate?: string;
  status?: 'active' | 'deceased' | 'transferred';
  deceasedAt?: string;
  deceasedReason?: string;
}

export interface UpdateAnimalRequest extends Partial<CreateAnimalRequest> {
  id: string;
}

class AnimalsService {
  private isClientRole(): boolean {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { role?: string };
      return parsed.role === 'client';
    } catch {
      return false;
    }
  }

  private getPatientsBasePath(): string {
    return this.isClientRole() ? '/client/patients' : '/patients';
  }

  private convertToBackendFormat(animal: CreateAnimalRequest | UpdateAnimalRequest): any {
    const backendData: any = {
      ...animal,
      owner_id: localStorage.getItem('userId'), // Adauga owner_id din localStorage
    };

    // Converteste campurile din camelCase in snake_case pentru backend
    // IMPORTANT: backend-ul de POST /patients asteapta microchipId (camelCase).
    // Pastram microchipId si adaugam in plus microchip_id pentru compatibilitate.
    if (animal.microchipId !== undefined) {
      backendData.microchip_id = animal.microchipId;
      // NU sterge microchipId – este folosit de backend la POST /patients
    }

    if (animal.birthDate !== undefined) {
      backendData.birth_date = animal.birthDate;
      delete backendData.birthDate;
    }

    if (animal.neuteredDate !== undefined) {
      backendData.neuteredDate = animal.neuteredDate;
    }

    if (animal.isNeutered !== undefined) {
      backendData.isNeutered = animal.isNeutered;
    }

    // profileImage ramane neschimbat pentru ca backend-ul foloseste acelasi nume
    // microchipId -> microchip_id
    // birthDate -> birth_date

    return backendData;
  }

  private convertToFrontendFormat(animal: any): Animal {
    
    const convertedAnimal = {
      id: animal.id,
      name: animal.name,
      species: animal.species,
      breed: animal.breed,
      gender: animal.gender,
      birthDate: animal.birthDate,
      weight: animal.weight,
      color: animal.color,
      microchipId: animal.microchipId, // Backend returneaza microchipId (camelCase)
      allergies: animal.allergies || [],
      chronicConditions: animal.chronicConditions || [],
      medications: animal.medications || [],
      profileImage: animal.profileImage,
      isActive: animal.isActive,
      ownerId: animal.ownerId,
      createdAt: animal.createdAt,
      updatedAt: animal.updatedAt,
      isNeutered: animal.isNeutered || animal.is_neutered || false,
      neuteredDate: animal.neuteredDate || animal.neutered_date || undefined,
    };
    
    
    return convertedAnimal;
  }

  async getAnimals(): Promise<Animal[]> {
    try {
      const response = await apiClient.get(this.getPatientsBasePath());
      const data = response.data;
      const source = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.patients)
          ? data.data.patients
          : [];
      const convertedAnimals = source.map((animal: any) => this.convertToFrontendFormat(animal));
      return convertedAnimals;
    } catch (error) {
      console.error('Error fetching animals:', error);
      throw new Error('Eroare la conectarea cu serverul');
    }
  }

  async getAnimal(id: string): Promise<Animal> {
    try {
      const response = await apiClient.get(`${this.getPatientsBasePath()}/${id}`);
      const payload = response.data?.data?.patient || response.data?.data;
      return this.convertToFrontendFormat(payload);
    } catch (error) {
      console.error('Error fetching animal:', error);
      throw new Error('Eroare la conectarea cu serverul');
    }
  }

  async createAnimal(animalData: CreateAnimalRequest): Promise<Animal> {
    try {
      const backendData = this.convertToBackendFormat(animalData);
      const response = await apiClient.post(this.getPatientsBasePath(), backendData);
      return this.convertToFrontendFormat(response.data.data);
    } catch (error) {
      console.error('Error creating animal:', error);
      throw new Error('Eroare la conectarea cu serverul');
    }
  }

  async updateAnimal(id: string, animalData: Partial<CreateAnimalRequest>): Promise<Animal> {
    try {
      const backendData = this.convertToBackendFormat({ ...animalData, id });
      const response = await apiClient.put(`${this.getPatientsBasePath()}/${id}`, backendData);
      return this.convertToFrontendFormat(response.data.data);
    } catch (error) {
      console.error('Error updating animal:', error);
      throw new Error('Eroare la conectarea cu serverul');
    }
  }

  async deleteAnimal(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.getPatientsBasePath()}/${id}`);
    } catch (error) {
      console.error('Error deleting animal:', error);
      throw new Error('Eroare la conectarea cu serverul');
    }
  }

  async getSpeciesList(): Promise<string[]> {
    return ['dog', 'cat', 'bird', 'rabbit', 'horse', 'other'];
  }
}

export const animalsService = new AnimalsService(); 
